import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';

/**
 * User Profile Functions
 */
export const getUserProfile = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, profileData) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      ...profileData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Orders Functions
 */
export const getUserOrders = async (userId) => {
  try {
    const ordersCollectionRef = collection(db, 'orders');
    const ordersQuery = query(
      ordersCollectionRef, 
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const orderSnapshot = await getDocs(ordersQuery);
    
    return orderSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user orders:', error);
    // Provide a clearer message when the composite index is missing
    if (error && (error.code === 'failed-precondition' || String(error.message || '').toLowerCase().includes('index'))) {
      const friendly = new Error(
        'Firestore requires a composite index for orders query: where(userId ==) + orderBy(createdAt desc). Create an index on orders with fields userId (ASC) and createdAt (DESC) in the Firebase console.'
      );
      friendly.code = error.code;
      throw friendly;
    }
    throw error;
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const orderDocRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderDocRef);
    
    if (orderDoc.exists()) {
      return { id: orderDoc.id, ...orderDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting order details:', error);
    throw error;
  }
};

export const createOrder = async (orderData) => {
  try {
    // Create a reference to the user document
    const userRef = doc(db, 'users', orderData.userId);
    
    const ordersCollectionRef = collection(db, 'orders');
    
    const newOrder = {
      ...orderData,
      userRef: userRef,  // Store both reference and ID
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'processing',
      statusHistory: [
        {
          status: 'created',
          timestamp: serverTimestamp(),
          note: 'Order placed'
        },
        {
          status: 'processing',
          timestamp: serverTimestamp(),
          note: 'Payment confirmed'
        }
      ]
    };
    
    const docRef = await addDoc(ordersCollectionRef, newOrder);
    return { id: docRef.id, ...newOrder };
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status, note = '') => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderDoc.data();
    
    // Add new status to history
    const statusHistory = orderData.statusHistory || [];
    statusHistory.push({
      status,
      timestamp: serverTimestamp(),
      note
    });
    
    // Update the order
    await updateDoc(orderRef, {
      status,
      statusHistory,
      updatedAt: serverTimestamp()
    });
    
    return {
      id: orderId,
      status,
      updatedAt: serverTimestamp()
    };
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Addresses Functions
 */
export const getUserAddresses = async (userId) => {
  try {
    const addressesCollectionRef = collection(db, 'users', userId, 'addresses');
    const addressesSnapshot = await getDocs(addressesCollectionRef);
    
    return addressesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user addresses:', error);
    throw error;
  }
};

export const addAddress = async (userId, addressData) => {
  try {
    const addressesCollectionRef = collection(db, 'users', userId, 'addresses');
    
    // If this is the first address or marked as default, ensure it's set as default
    const addresses = await getUserAddresses(userId);
    const isDefault = addressData.default || addresses.length === 0;
    
    // If this is a new default address, update any existing default address
    if (isDefault) {
      await updateDefaultAddress(userId, null);
    }
    
    const newAddress = {
      ...addressData,
      default: isDefault,
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(addressesCollectionRef, newAddress);
    return { id: docRef.id, ...newAddress };
  } catch (error) {
    console.error('Error adding address:', error);
    throw error;
  }
};

export const updateAddress = async (userId, addressId, addressData) => {
  try {
    const addressDocRef = doc(db, 'users', userId, 'addresses', addressId);
    
    // If setting as default, update any existing default address
    if (addressData.default) {
      await updateDefaultAddress(userId, addressId);
    }
    
    await updateDoc(addressDocRef, {
      ...addressData,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating address:', error);
    throw error;
  }
};

export const deleteAddress = async (userId, addressId) => {
  try {
    const addressDocRef = doc(db, 'users', userId, 'addresses', addressId);
    await deleteDoc(addressDocRef);
    return true;
  } catch (error) {
    console.error('Error deleting address:', error);
    throw error;
  }
};

// Helper function to update default address
const updateDefaultAddress = async (userId, newDefaultAddressId) => {
  try {
    const addressesCollectionRef = collection(db, 'users', userId, 'addresses');
    const addressesSnapshot = await getDocs(query(addressesCollectionRef, where('default', '==', true)));
    
    const updatePromises = addressesSnapshot.docs.map(doc => {
      if (doc.id !== newDefaultAddressId) {
        return updateDoc(doc.ref, { default: false });
      }
      return Promise.resolve();
    });
    
    await Promise.all(updatePromises);
    return true;
  } catch (error) {
    console.error('Error updating default address:', error);
    throw error;
  }
};

/**
 * Payment Methods Functions
 */
export const getUserPaymentMethods = async (userId) => {
  try {
    const paymentMethodsCollectionRef = collection(db, 'users', userId, 'paymentMethods');
    const paymentMethodsSnapshot = await getDocs(paymentMethodsCollectionRef);
    
    return paymentMethodsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user payment methods:', error);
    throw error;
  }
};

export const addPaymentMethod = async (userId, paymentData) => {
  try {
    const paymentMethodsCollectionRef = collection(db, 'users', userId, 'paymentMethods');
    
    // If this is the first payment method or marked as default, ensure it's set as default
    const paymentMethods = await getUserPaymentMethods(userId);
    const isDefault = paymentData.default || paymentMethods.length === 0;
    
    // If this is a new default payment method, update any existing default payment method
    if (isDefault) {
      await updateDefaultPaymentMethod(userId, null);
    }
    
    const newPaymentMethod = {
      ...paymentData,
      default: isDefault,
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(paymentMethodsCollectionRef, newPaymentMethod);
    return { id: docRef.id, ...newPaymentMethod };
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
};

export const updatePaymentMethod = async (userId, paymentId, paymentData) => {
  try {
    const paymentDocRef = doc(db, 'users', userId, 'paymentMethods', paymentId);
    
    // If setting as default, update any existing default payment method
    if (paymentData.default) {
      await updateDefaultPaymentMethod(userId, paymentId);
    }
    
    await updateDoc(paymentDocRef, {
      ...paymentData,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw error;
  }
};

export const deletePaymentMethod = async (userId, paymentId) => {
  try {
    const paymentDocRef = doc(db, 'users', userId, 'paymentMethods', paymentId);
    await deleteDoc(paymentDocRef);
    return true;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    throw error;
  }
};

// Helper function to update default payment method
const updateDefaultPaymentMethod = async (userId, newDefaultPaymentId) => {
  try {
    const paymentMethodsCollectionRef = collection(db, 'users', userId, 'paymentMethods');
    const paymentMethodsSnapshot = await getDocs(query(paymentMethodsCollectionRef, where('default', '==', true)));
    
    const updatePromises = paymentMethodsSnapshot.docs.map(doc => {
      if (doc.id !== newDefaultPaymentId) {
        return updateDoc(doc.ref, { default: false });
      }
      return Promise.resolve();
    });
    
    await Promise.all(updatePromises);
    return true;
  } catch (error) {
    console.error('Error updating default payment method:', error);
    throw error;
  }
};

/**
 * Security Functions
 */
export const updateUserPassword = async (userId, currentPassword, newPassword) => {
  // Note: Firebase Auth handles password updates directly
  // This is a placeholder for any additional logic you might want to add
  // The actual password update would be handled by Firebase Auth
  console.log('Password update would be handled by Firebase Auth');
  return true;
};
